import { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  RowSelectionState,
} from '@tanstack/react-table'
import { useRuns, useRetryRun, useBulkDeleteRuns, useBulkRetryRuns } from '@/hooks/use-runs'
import { DataTable } from '@/components/data-table/DataTable'
import { DataTablePagination } from '@/components/data-table/DataTablePagination'
import { DataTableToolbar, type Filters } from '@/components/data-table/DataTableToolbar'
import { createColumns } from '@/components/data-table/columns'
import type { Run } from '@/api/types'

export function RunsList() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Table state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // Initialize filters from URL params
  const [filters, setFilters] = useState<Filters>(() => {
    const phenotype = searchParams.get('phenotype')
    return phenotype ? { phenotype } : {}
  })

  // Mutations
  const retryMutation = useRetryRun()
  const bulkDeleteMutation = useBulkDeleteRuns()
  const bulkRetryMutation = useBulkRetryRuns()

  // Convert filters to API format
  const apiFilters = useMemo(
    () => ({
      status: filters.status ? [filters.status] : undefined,
      workflow: filters.workflow ? [filters.workflow] : undefined,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [filters, pagination]
  )

  // Fetch runs with filters and pagination
  const { data: runs, isLoading, isError } = useRuns(apiFilters)

  // Filter runs client-side for search and phenotype (backend may not support these filters)
  const filteredRuns = useMemo(() => {
    if (!runs) return []

    let result = runs

    // Filter by phenotype
    if (filters.phenotype) {
      result = result.filter((run) => run.phenotype === filters.phenotype)
    }

    // Filter by search
    if (filters.search) {
      const search = filters.search.toLowerCase()
      result = result.filter((run) =>
        (run.fileName || run.originalName || run.fileId).toLowerCase().includes(search)
      )
    }

    return result
  }, [runs, filters.search, filters.phenotype])

  // Column definitions with actions
  const columns = useMemo(
    () =>
      createColumns({
        onView: (run) => navigate(`/runs/${run.fileId}`),
        onRetry: (run) => retryMutation.mutate(run.fileId),
        onDelete: (run) => {
          if (confirm(`Delete run "${run.fileName || run.fileId}"?`)) {
            bulkDeleteMutation.mutate([run.fileId])
          }
        },
      }),
    [navigate, retryMutation, bulkDeleteMutation]
  )

  // Configure TanStack Table
  // Note: Using client-side pagination for now
  // When backend supports server-side pagination (returns total count),
  // switch to manualPagination: true and provide pageCount
  const table = useReactTable({
    data: filteredRuns,
    columns,
    state: {
      pagination,
      sorting,
      rowSelection,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.fileId,
    enableRowSelection: true,
  })

  // Handle bulk actions
  const handleBulkDelete = (ids: string[]) => {
    if (confirm(`Delete ${ids.length} selected runs?`)) {
      bulkDeleteMutation.mutate(ids, {
        onSuccess: () => {
          table.resetRowSelection()
        },
      })
    }
  }

  const handleBulkRetry = (ids: string[]) => {
    bulkRetryMutation.mutate(ids, {
      onSuccess: () => {
        table.resetRowSelection()
      },
    })
  }

  // Handle row click navigation
  const handleRowClick = (run: Run) => {
    navigate(`/runs/${run.fileId}`)
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Extraction Runs</h1>
        <div className="rounded-md border border-destructive p-4 text-destructive">
          Failed to load runs. Please try refreshing the page.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Extraction Runs</h1>
        <p className="text-muted-foreground">Browse and manage all extraction runs</p>
      </div>

      <DataTableToolbar table={table} filters={filters} onFiltersChange={setFilters} />

      <DataTable
        table={table}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        onBulkDelete={handleBulkDelete}
        onBulkRetry={handleBulkRetry}
        getRowId={(run) => run.fileId}
      />

      <DataTablePagination table={table} />
    </div>
  )
}
